import axios from 'axios';

export class DID {
    static MAX_RETRY_COUNT = 3;
    static MAX_DELAY_SECONDS = 4;
    static DID_KEY = "YXR0YW5hc2VlbTMwNkBnbWFpbC5jb20:LnrABpFhHgUebeDHNwJes";
    static DID_SERVICE = "talks";
    static DID_URL = "https://api.d-id.com";
    static SOURCE_URL = "https://create-images-results.d-id.com/google-oauth2%7C112420254033369632535/upl_gmuSUDuWyz-KPGC4h-G37/thumbnail.jpeg";

    static createAxiosInstance() {
        axios.defaults.baseURL = `${DID.DID_URL}`;
        axios.defaults.headers.common["Authorization"] = `Basic ${DID.DID_KEY}`;
        axios.defaults.headers.common["content-type"] = "application/json";
        return axios;
    }

    static async setupWebRTCStream(videoElement, inputValue) {
        try {
            // Step 1: Create Agent and Chat
            const { agentId } = await DID.createAgent();
            const { chatId } = await DID.createChat(agentId);

            // Step 2: Create Stream and Fetch ICE Servers and SDP Offer
            const { streamId, sessionId, offer, iceServers } = await DID.createStream(chatId);

            if (!iceServers || !offer) {
                console.error("ICE servers or SDP offer not provided.");
                return;
            }

            // Step 3: Initialize RTCPeerConnection with ICE servers
            const peer = new RTCPeerConnection({ iceServers });

            // Step 4: Handle the incoming stream and attach to video element
            peer.ontrack = (event) => {
                if (event.track) {
                    videoElement.srcObject = event.streams[0];
                    videoElement.play().catch((error) => console.error("Video play error:", error));
                    // setIsVideoReady(true);
                    console.log("Remote stream received:", event.streams[0]);
                }
            };

            // Step 5: Handle ICE candidates
            peer.onicecandidate = (event) => {
                if (event.candidate) {
                    console.log("Local ICE candidate:", event.candidate);
                    // Send ICE candidates to the DID service
                    DID.createIce({
                        streamId,
                        candidate: event.candidate.candidate,
                        sdpMid: event.candidate.sdpMid,
                        sdpMLineIndex: event.candidate.sdpMLineIndex,
                        sessionId,
                    });
                }
            };

            // Step 6: Set remote description (SDP offer) and create an SDP answer
            await peer.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await peer.createAnswer();
            await peer.setLocalDescription(answer);

            // Step 7: Send the SDP answer to the DID service
            const { sessionId: newSessionId } = await DID.createSdp(answer, sessionId, streamId);
            console.log("SDP answer created and set.");

            // Step 8: Send a message to the agent (optional)
            await DID.createMessage({
                agentId,
                chatId,
                streamId,
                sessionId: newSessionId,
                txtAreaValue: inputValue,
            });
        } catch (error) {
            console.error("Error setting up WebRTC stream:", error);
        }
    }

    static async createAgent() {
        try {
            const axiosInstance = DID.createAxiosInstance();
            const agentResponse = await axiosInstance.post("/agents", {
                presenter: {
                    type: "talk",
                    voice: {
                        type: "microsoft",
                        voice_id: "en-US-JennyMultilingualV2Neural",
                    },
                    thumbnail: DID.SOURCE_URL,
                    source_url: DID.SOURCE_URL,
                },
                llm: {
                    type: "openai",
                    provider: "openai",
                    model: "gpt-3.5-turbo-1106",
                    instructions: "Your name is Emma, an AI designed to assist with information about Prompt Engineering and RAG",
                },
                preview_name: "Emma",
            });

            console.log({ agentResponse });

            return { agentId: agentResponse.data.id };
        } catch (error) {
            console.error("Error creating agent:", error);
        }
    }

    static async createChat(agentId) {
        try {
            const axiosInstance = DID.createAxiosInstance();
            const chatResponse = await axiosInstance.post(`/agents/${agentId}/chat`);
            console.log({ chatResponse });
            return { chatId: chatResponse.data.id };
        } catch (error) {
            console.error("Error creating chat:", error);
        }
    }

    static async createStream(chatId) {
        try {
            const axiosInstance = DID.createAxiosInstance();
            const streamResponse = await axiosInstance.post(`${DID.DID_URL}/${DID.DID_SERVICE}/streams`, {
                source_url: DID.SOURCE_URL,
            });

            console.log({ streamResponse });

            return {
                streamId: streamResponse.data.id,
                sessionId: streamResponse.data.session_id,
                offer: streamResponse.data.offer,
                iceServers: streamResponse.data.ice_servers,
            };
        } catch (error) {
            console.error("Error creating stream:", error);
        }
    }

    static async createSdp(answer, sessionId, streamId) {
        try {
            const axiosInstance = DID.createAxiosInstance();
            const sdpResponse = await axiosInstance.post(`${DID.DID_URL}/${DID.DID_SERVICE}/streams/${streamId}/sdp`, {
                answer: answer,
                session_id: sessionId,
            });

            console.log({ sdpResponse });

            return { sessionId: sdpResponse.data.session_id };
        } catch (error) {
            console.error("Error creating SDP:", error);
        }
    }

    static async createIce({ streamId, candidate, sdpMid, sdpMLineIndex, sessionId }) {
        try {
            const axiosInstance = DID.createAxiosInstance();
            const iceResponse = await axiosInstance.post(`${DID.DID_URL}/${DID.DID_SERVICE}/streams/${streamId}/ice`, {
                candidate,
                sdpMid,
                sdpMLineIndex,
                session_id: sessionId,
            });
            console.log({ iceResponse });
        } catch (error) {
            console.error("Error creating ICE candidate:", error);
        }
    }

    static async createMessage({ agentId, chatId, streamId, sessionId, txtAreaValue }) {
        try {
            const url = `${DID.DID_URL}/agents/${agentId}/chat/${chatId}`;
            const messageResponse = await DID.fetchWithRetries(url, {
                streamId: streamId,
                sessionId: sessionId,
                messages: [
                    {
                        role: "user",
                        content: txtAreaValue,
                        created_at: new Date().toString(),
                    },
                ],
            });

            if (messageResponse.status === 200 && messageResponse.chatMode === "TextOnly") {
                console.log("User is out of credit, API only return text messages");
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }
    }

    static async fetchWithRetries(url, options, retries = 1) {
        try {
            const axiosInstance = DID.createAxiosInstance();
            return await axiosInstance.post(url, options);
        } catch (err) {
            if (retries <= DID.MAX_RETRY_COUNT) {
                const delay =
                    Math.min(Math.pow(2, retries) / 4 + Math.random(), DID.MAX_DELAY_SECONDS) * 1000;

                await new Promise((resolve) => setTimeout(resolve, delay));

                console.log(
                    `Request failed, retrying ${retries}/${DID.MAX_RETRY_COUNT}. Error ${err}`
                );
                return DID.fetchWithRetries(url, options, retries + 1);
            } else {
                throw new Error(`Max retries exceeded. error: ${err}`);
            }
        }
    }
}
